import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IoSearch, IoClose, IoChatbubbleEllipsesOutline, IoEllipseOutline, IoSparkles, IoHourglass, IoDocument } from 'react-icons/io5';
import { BiSortAZ, BiSortZA, BiSortUp, BiSortDown } from 'react-icons/bi';
import { TbCircles } from 'react-icons/tb';
import { MdOutlineLink } from 'react-icons/md';
import '../style/NodeListSidebar.css';
import focusOnNode from '../helpers/focusOnNode';

/**
 * NodeListSidebar Component
 * 
 * A collapsible sidebar that displays a searchable list of all nodes in the graph.
 * 
 * Features:
 * - Search functionality to filter nodes by label or title
 * - Click on a node to focus and zoom on it in the graph
 * - Quick access to chat about any node
 * - Display node statistics (connections, page index, etc.)
 * - Keyboard shortcut: Press 'N' to toggle the sidebar
 * - Sort nodes by importance (main, selected, loading, etc.)
 * 
 * @param {Object} graph - The graph object containing nodes and edges
 * @param {Object} networkRef - Reference to the vis-network instance
 * @param {boolean} isOpen - Whether the sidebar is open
 * @param {Function} onToggle - Callback to toggle sidebar open/closed
 * @param {Function} onNodeSelect - Callback when a node is selected
 * @param {Function} onNodeChat - Callback to open chat for a node
 */
const NodeListSidebar = ({ graph, networkRef, isOpen, onToggle, onNodeSelect, onNodeChat }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [sortBy, setSortBy] = useState('order');

  // Keyboard shortcut to toggle sidebar (N key)
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only trigger if not typing in an input/textarea and not in a modal
      if (
        e.key?.toLowerCase() === 'n' &&
        !e.ctrlKey && 
        !e.metaKey && 
        !e.altKey &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA' &&
        !document.activeElement.isContentEditable
      ) {
        onToggle();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onToggle]);

  // Pre-compute edge counts in a single O(m) pass for O(1) lookups
  const edgeCounts = useMemo(() => {
    const outgoing = new Map();
    const incoming = new Map();
    if (graph?.edges) {
      graph.edges.forEach(edge => {
        outgoing.set(edge.from, (outgoing.get(edge.from) || 0) + 1);
        incoming.set(edge.to, (incoming.get(edge.to) || 0) + 1);
      });
    }
    return { outgoing, incoming };
  }, [graph?.edges]);

  // Filter and sort nodes
  const filteredNodes = useMemo(() => {
    if (!graph || !graph.nodes) return [];
    
    const filtered = graph.nodes.filter(node => {
      const label = node.label || '';
      const title = node.title || '';
      const searchLower = searchTerm.toLowerCase();
      return label.toLowerCase().includes(searchLower) || 
             title.toLowerCase().includes(searchLower);
    });

    // Sort based on selected option
    const sorted = filtered.sort((a, b) => {
      let primarySort = 0;
      
      // Primary sort: by selected option
      if (sortBy === 'name') {
        const aLabel = (a.label || '').toLowerCase();
        const bLabel = (b.label || '').toLowerCase();
        primarySort = aLabel.localeCompare(bLabel);
      } else if (sortBy === 'name-desc') {
        const aLabel = (a.label || '').toLowerCase();
        const bLabel = (b.label || '').toLowerCase();
        primarySort = bLabel.localeCompare(aLabel);
      } else if (sortBy === 'links') {
        const aLinks = edgeCounts.outgoing.get(a.id) || 0;
        const bLinks = edgeCounts.outgoing.get(b.id) || 0;
        primarySort = aLinks - bLinks;
      } else if (sortBy === 'links-desc') {
        const aLinks = edgeCounts.outgoing.get(a.id) || 0;
        const bLinks = edgeCounts.outgoing.get(b.id) || 0;
        primarySort = bLinks - aLinks;
      } else if (sortBy === 'refs') {
        const aRefs = edgeCounts.incoming.get(a.id) || 0;
        const bRefs = edgeCounts.incoming.get(b.id) || 0;
        primarySort = aRefs - bRefs;
      } else if (sortBy === 'refs-desc') {
        const aRefs = edgeCounts.incoming.get(a.id) || 0;
        const bRefs = edgeCounts.incoming.get(b.id) || 0;
        primarySort = bRefs - aRefs;
      } else if (sortBy === 'order') {
        const aOrder = a.orderIndex ?? Infinity;
        const bOrder = b.orderIndex ?? Infinity;
        primarySort = aOrder - bOrder;
      } else if (sortBy === 'order-desc') {
        const aOrder = a.orderIndex ?? -1;
        const bOrder = b.orderIndex ?? -1;
        primarySort = bOrder - aOrder;
      }
      
      // If primary sort gives a result, use it
      if (primarySort !== 0) {
        return primarySort;
      }
      
      // Secondary sort: by group priority (main → selected → loading → default)
      const groupOrder = { main: 0, selected: 1, loading: 2 };
      const aOrder = groupOrder[a.group] !== undefined ? groupOrder[a.group] : 3;
      const bOrder = groupOrder[b.group] !== undefined ? groupOrder[b.group] : 3;

      // Sort by group order
      return aOrder - bOrder;
    });

    // Group nodes by category: file node first, then others
    const fileNodes = sorted.filter(node =>
      node.id === 1 && graph.topic
    );

    const otherNodes = sorted.filter(node =>
      !(node.id === 1 && graph.topic)
    );

    // Return grouped array: [file, others...]
    return [...fileNodes, ...otherNodes];
  }, [graph, searchTerm, sortBy, edgeCounts]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!graph || !graph.nodes) return { total: 0, edges: 0 };
    
    return {
      total: filteredNodes.length,
      edges: graph.edges ? graph.edges.length : 0,
      selected: filteredNodes.filter(n => n.group === 'selected').length
    };
  }, [filteredNodes, graph]);

  const handleNodeClick = (node, event) => {
    // Don't trigger if clicking on action buttons
    if (event.target.closest('.node-actions')) {
      return;
    }
    
    if (!networkRef || !networkRef.current) return;
    
    setSelectedNodeId(node.id);
    
    // Focus on the node in the graph
    // Scale: smaller = more distant/zoomed out, larger = more zoomed in
    focusOnNode(node.id, networkRef);
    
    // Call the parent callback if provided
    if (onNodeSelect) {
      onNodeSelect(node);
    }
  };

  const handleTouchStart = (node, event) => {
    // Don't trigger if touching on action buttons
    if (event.target.closest('.node-actions')) {
      return;
    }
    
    // Prevent the click event from firing after touch (to avoid double-trigger)
    event.preventDefault();
    
    // Create a synthetic event object for handleNodeClick
    const syntheticEvent = {
      target: event.target,
      currentTarget: event.currentTarget,
      preventDefault: () => {},
      stopPropagation: () => {},
      closest: (selector) => event.target.closest(selector)
    };
    
    handleNodeClick(node, syntheticEvent);
  };

  const handleChatClick = (node, event) => {
    event.stopPropagation();
    
    if (onNodeChat) {
      onNodeChat(node);
    }
  };

  const cycleSortOrder = () => {
    const sortOptions = ['name', 'name-desc', 'links', 'links-desc', 'refs', 'refs-desc', 'order', 'order-desc'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % sortOptions.length;
    setSortBy(sortOptions[nextIndex]);
  };

  const getSortIcon = () => {
    switch (sortBy) {
      case 'name':
        return <BiSortAZ size={14} />;
      case 'name-desc':
        return <BiSortZA size={14} />;
      case 'links':
        return <MdOutlineLink size={14} />;
      case 'links-desc':
        return <MdOutlineLink size={14} style={{ transform: 'scaleY(-1)' }} />;
      case 'refs':
        return <MdOutlineLink size={14} />;
      case 'refs-desc':
        return <MdOutlineLink size={14} style={{ transform: 'scaleY(-1)' }} />;
      case 'order':
        return <BiSortUp size={14} />;
      case 'order-desc':
        return <BiSortDown size={14} />;
      default:
        return <BiSortAZ size={14} />;
    }
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'name':
        return t('nodeList.sortAZ');
      case 'name-desc':
        return t('nodeList.sortZA');
      case 'links':
        return t('nodeList.sortFromAsc');
      case 'links-desc':
        return t('nodeList.sortFromDesc');
      case 'refs':
        return t('nodeList.sortToAsc');
      case 'refs-desc':
        return t('nodeList.sortToDesc');
      case 'order':
        return t('nodeList.sortOrderAsc', '#↑');
      case 'order-desc':
        return t('nodeList.sortOrderDesc', '#↓');
      default:
        return t('nodeList.sortAZ');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const getNodeIcon = (node) => {
    // Check for file nodes (main node in file graphs, typically id: 1)
    if (node.id === 1 && graph.topic) {
      return <IoDocument size={20} />;
    }

    // Then check by group
    switch (node.group) {
      case 'selected':
        return <IoSparkles size={20} />;
      case 'loading':
        return <IoHourglass size={20} />;
      default:
        return <IoEllipseOutline size={20} />;
    }
  };

  const getNodeBadge = (node) => {
    const badges = [];

    if (node.orderIndex != null) {
      badges.push(
        <span key="order" className="node-badge order">
          #{node.orderIndex}
        </span>
      );
    }

    // O(1) lookup from pre-computed edge counts
    const links = edgeCounts.outgoing.get(node.id) || 0;

    if (links > 0) {
      badges.push(
        <span key="links" className="node-badge connections">
          {links} FROM
        </span>
      );
    }

    const references = edgeCounts.incoming.get(node.id) || 0;

    if (references > 0) {
      badges.push(
        <span key="references" className="node-badge references">
          {references} TO
        </span>
      );
    }

    return badges.length > 0 ? <div className="node-badges">{badges}</div> : null;
  };

  return (
    <>
      {/* Sidebar */}
      <div className={`node-list-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="node-list-header">
          <div className="node-list-title-container">
            <h3 className="node-list-title">
              <TbCircles size={20} />
              {t('nodeList.title')}
            </h3>
            <span className="keyboard-hint">{t('nodeList.pressNToToggle')}</span>
          </div>
          <button 
            className="node-list-close-btn"
            onClick={onToggle}
            title={t('nodeList.closeOrPressN')}
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Search bar */}
        <div className="node-list-search">
          <IoSearch className="search-icon" size={18} />
          <input
            type="text"
            placeholder={t('nodeList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search-btn"
              onClick={clearSearch}
              title={t('nodeList.clearSearch')}
            >
              <IoClose size={18} />
            </button>
          )}
        </div>

        {/* Node count and actions */}
        <div className="node-list-stats">
          <div className="stats-info">
            <span className="stat-item">
              {stats.total} {stats.total === 1 ? t('nodeList.node') : t('nodeList.nodes')}
              {searchTerm && ` ${t('nodeList.found')}`}
            </span>
            {!searchTerm && stats.edges > 0 && (
              <span className="stat-item stat-secondary">
                {stats.edges} {stats.edges === 1 ? t('nodeList.edge') : t('nodeList.edges')}
              </span>
            )}
          </div>
          <div className="stats-actions">
            <button
              className="sort-btn"
              onClick={cycleSortOrder}
              title={t('nodeList.sortBy', { label: getSortLabel() })}
            >
              {getSortIcon()}
              <span className="sort-label">{getSortLabel()}</span>
            </button>
          </div>
        </div>

        {/* Node list */}
        <div className="node-list-container">
          {filteredNodes.length === 0 ? (
            <div className="node-list-empty">
              {searchTerm ? t('nodeList.noNodesFound') : t('nodeList.noNodesAvailable')}
            </div>
          ) : (
            <ul className="node-list">
              {filteredNodes.map((node) => (
                <li 
                  key={node.id} 
                  className={`node-list-item ${selectedNodeId === node.id ? 'selected' : ''} ${node.group || ''}`}
                  onClick={(e) => handleNodeClick(node, e)}
                  onTouchStart={(e) => handleTouchStart(node, e)}
                  title={node.title || node.label}
                >
                  <span className="node-icon">{getNodeIcon(node)}</span>
                  <div className="node-info">
                    <span className="node-label">
                      {node.label || t('nodeList.untitledNode')}
                    </span>
                    {getNodeBadge(node)}
                  </div>
                  <div className="node-actions">
                    <button
                      className="node-action-btn"
                      onClick={(e) => handleChatClick(node, e)}
                      title={t('nodeList.chatAboutNode')}
                    >
                      <IoChatbubbleEllipsesOutline size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default NodeListSidebar;

